import callApi, { BaseResponse, BaseResponseDto } from "./baseRequest";

const route = 'export-view';

export const ORDERS_EXPORT_REPORT_TYPE = 1;

export interface ExportViewColumnDefinition {
    id: string;
    code: number;
    namePt: string;
    nameEs: string;
    nameEn: string;
    dataType: string;
    active: boolean;
    reportType: number;
}

export interface ExportViewColumn {
    id: string;
    columnId: string;
    displayNamePt: string;
    displayNameEs: string;
    displayNameEn: string;
    sortOrder: number;
}

export interface ExportView {
    id: string;
    userEntityId: string;
    customerId: string;
    name: string;
    isDefault: boolean;
    active: boolean;
    createdAt: string;
    lastUpdate: string | null;
    columns: ExportViewColumn[];
}

export interface CreateExportViewColumn {
    displayNamePt: string;
    displayNameEs: string;
    displayNameEn: string;
    sortOrder: number;
    columnId: string;
}

export interface CreateExportViewRequest {
    userEntityId: string;
    customerId: string;
    reportType?: number;
    name: string;
    isDefault: boolean;
    columns: CreateExportViewColumn[];
}

const emptyErrorResult = <T>(data: T): BaseResponseDto<T> => ({
    data,
    error: '',
    errorPT: '',
    errorEN: '',
    errorES: '',
    success: false
});

const applyError = <T>(result: BaseResponseDto<T>, err: any) => {
    result.error = err?.error ?? err;
    result.errorPT = err?.errorPT;
    result.errorES = err?.errorES;
    result.errorEN = err?.errorEN;
    result.errors = err?.errors;
    return result;
};

export const normalizeExportViewColumns = (columns?: ExportViewColumnDefinition[] | null): ExportViewColumnDefinition[] => {
    return (columns || [])
        .filter(column => column.active !== false)
        .sort((a, b) => a.code - b.code);
};

export const normalizeExportViews = (views?: ExportView[] | null): ExportView[] => {
    return (views || [])
        .filter(view => view.active !== false)
        .map(view => ({
            ...view,
            columns: [...(view.columns || [])].sort((a, b) => a.sortOrder - b.sortOrder)
        }));
};

export function getExportViewColumns(reportType = ORDERS_EXPORT_REPORT_TYPE): Promise<BaseResponseDto<ExportViewColumnDefinition[]>> {
    const result = emptyErrorResult<ExportViewColumnDefinition[]>([]);

    return callApi('GET', `${route}/columns?reportType=${reportType}`)
        .then(resp => {
            if (resp) {
                result.data = normalizeExportViewColumns(resp.data);
                result.success = true;
            }
            return result;
        })
        .catch(err => applyError(result, err));
}

export function getExportViews(userId: string, customerId: string, exportViewId?: string, reportType = ORDERS_EXPORT_REPORT_TYPE): Promise<BaseResponseDto<ExportView[]>> {
    const result = emptyErrorResult<ExportView[]>([]);
    const selectedViewQuery = exportViewId?.trim()
        ? `&exportViewId=${encodeURIComponent(exportViewId.trim())}`
        : '';

    return callApi(
        'GET',
        `${route}?userId=${encodeURIComponent(userId)}&customerId=${encodeURIComponent(customerId)}&reportType=${reportType}${selectedViewQuery}`
    )
        .then(resp => {
            if (resp) {
                result.data = normalizeExportViews(resp.data);
                result.success = true;
            }
            return result;
        })
        .catch(err => applyError(result, err));
}

export function createExportView(request: CreateExportViewRequest): Promise<BaseResponse> {
    const result: BaseResponse = {
        error: '',
        errorPT: '',
        errorEN: '',
        errorES: '',
        success: false
    };

    return callApi('POST', `${route}`, request)
        .then(() => {
            result.success = true;
            return result;
        })
        .catch(err => {
            result.error = err?.error ?? err;
            result.errorPT = err?.errorPT;
            result.errorES = err?.errorES;
            result.errorEN = err?.errorEN;
            return result;
        });
}

export function deleteExportView(id: string): Promise<BaseResponse> {
    const result: BaseResponse = {
        error: '',
        errorPT: '',
        errorEN: '',
        errorES: '',
        success: false
    };

    return callApi('DELETE', `${route}/${encodeURIComponent(id)}`)
        .then(() => {
            result.success = true;
            return result;
        })
        .catch(err => {
            result.error = err?.error ?? err;
            result.errorPT = err?.errorPT;
            result.errorES = err?.errorES;
            result.errorEN = err?.errorEN;
            return result;
        });
}

export function setDefaultExportView(id: string): Promise<BaseResponse> {
    const result: BaseResponse = {
        error: '',
        errorPT: '',
        errorEN: '',
        errorES: '',
        success: false
    };

    return callApi('PUT', `${route}/${encodeURIComponent(id)}`)
        .then(() => {
            result.success = true;
            return result;
        })
        .catch(err => {
            result.error = err?.error ?? err;
            result.errorPT = err?.errorPT;
            result.errorEN = err?.errorEN;
            result.errorES = err?.errorES;
            return result;
        });
}
